import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  Button,
  Icon,
  P,
  DataGrid,
  IconProps,
  Feedback,
  Lightbox,
} from '@fashionnovateam/nova-ui-toolkit';
import { Section } from '../ReusableComponents/NewDesign/Layout/Layout';
import Page from '../Page/index';
import Header from '../Header/index';
import PrinterPopover from '../ReusableComponents/PrinterConfiguration/PrinterPopover';
import StepSection from './StepSection';
import { VideoJS } from './VideoJS';
import { useGetPublicVideos } from '../../Lib/Hooks/videos.hooks';
import { PrintButtonCell } from '../PurchaseOrderDetail/PurchaseOrderDetailDataGridCells';
import { User } from '../../Lib/Api/vendor';
import {
  useGetPrintingOnboardingStatus,
  useUpdatePrintingOnboardingStatus,
} from '../../Lib/Hooks/printingOnboarding.hooks';
import { useBrowserPrint } from '../../Lib/Hooks/browserPrint.hooks';
import {
  generateTestZPL,
  PrintType,
} from '../../Lib/Utils/Asn/ZPLFileCreation/generate-test-label';
import { PrinterType } from '../ReusableComponents/PrinterConfiguration/types';
import UnitLabel from '../../Assets/images/unit.png';
import HangtagLabel from '../../Assets/images/hangtag.png';
import CartonLabel from '../../Assets/images/carton.png';

type PrintingSetupProps = {
  publicFilesUrls: {
    [key: string]: {
      read: string;
    };
  };
  user: User;
};

interface PrinterConfig {
  printerType: PrinterType;
}

type LightboxType = 'unit' | 'hangtag' | 'carton' | null;

export function PrintingSetupPage({
  publicFilesUrls,
  user,
}: PrintingSetupProps) {
  const unitLabelPhotos = [{ src: UnitLabel }];
  const hangtagPhotos = [{ src: HangtagLabel }];
  const cartonLabelPhotos = [{ src: CartonLabel }];

  const containerRef = useRef<HTMLDivElement>(null);
  const [isLightboxOpened, setIsLightboxOpened] = useState<LightboxType>(null);
  const { data: videoFiles } = useGetPublicVideos();

  const stepOne = [
    {
      id: '1',
      text: 'Purchase the printers and print supplies from the authorized sellers.',
    },
    {
      id: '2',
      text: 'We suggest you purchase 3 printers for the 3 types of labels - Unit Label, Carton Label, and Hangtag Stickers.',
    },
    {
      id: '3',
      text: 'Install Zebra Print Drivers specific to printer model purchased.',
    },
    {
      id: '4',
      text: 'Follow set up guidelines listed in the recommended driver support documentation.',
    },
  ];

  const stepTwo = [
    {
      id: '1',
      text: (
        <>
          Follow our installation guide for{' '}
          <a
            href={publicFilesUrls.mac_printing_guide?.read || '#'}
            className="text-primary-700 hover:text-primary-900"
          >
            Macintosh
          </a>{' '}
          or{' '}
          <a
            href={publicFilesUrls.windows_printing_guide?.read || '#'}
            className="text-primary-700 hover:text-primary-900"
          >
            PC.
          </a>
        </>
      ),
    },
    {
      id: '2',
      text: 'Once your printer is installed, configure each printer in the widget below. It will change from Not Set to the name of your printer.',
    },
  ];

  const stepThree = [
    {
      id: '1',
      text: 'Print the Unit Label, Hangtag and Carton Label below.',
    },
    {
      id: '2',
      text: 'Ship all of the printed test labels to us at:',
    },
  ];

  const buttonData = [
    { iconName: 'bag/paper-bag', label: 'Unit Labels' },
    { iconName: 'tag', label: 'HangTags' },
    { iconName: 'package/package-02', label: 'Carton Labels' },
  ];

  const { sendZplCmd, selectedDevices } = useBrowserPrint();

  const getPrinterConfig = (type: PrintType): PrinterConfig => {
    switch (type) {
      case 'unit':
        return {
          printerType: 'unitLabel',
        };
      case 'hangtag':
        return {
          printerType: 'hangTag',
        };
      case 'carton':
        return {
          printerType: 'cartonLabel',
        };
      default:
        return {
          printerType: 'unitLabel',
        };
    }
  };

  const generateAndPrintLabel = async (
    type: PrintType,
    sucessCallback: () => void,
  ) => {
    const labels = await generateTestZPL(user.id.toString(), type);
    if (!labels) return;

    const { printerType } = getPrinterConfig(type);

    console.log(`Test Label ZPL (${type}):`, labels);
    await sendZplCmd(
      user.id.toString(),
      printerType,
      labels?.zpl,
      labels?.request_id,
      sucessCallback,
      () => {
        toast.error(
          'Failed to print label. Please check your printing configuration and try again.',
        );
      },
    );
  };

  const updatePrintStatus = useUpdatePrintingOnboardingStatus();
  const { data: printStatus } = useGetPrintingOnboardingStatus(user.id);

  const handlePrintButtonClick = async (type: PrintType) => {
    try {
      const printerTypeMap: Record<PrintType, PrinterType> = {
        unit: 'unitLabel',
        carton: 'cartonLabel',
        hangtag: 'hangTag',
      };

      const printerType = printerTypeMap[type];
      const printerStatus = selectedDevices[printerType]?.status;

      if (printerStatus === 'offline') {
        toast.error('Printer is offline.');
        return;
      }

      if (printerStatus === 'not selected') {
        toast.error(
          'Your printer is not correctly configured or not ready to print.',
        );
        return;
      }

      if (printerStatus === 'selected') {
        toast.error('Your printer is not ready to print.');
        return;
      }

      await generateAndPrintLabel(type, async () => {
        await updatePrintStatus.mutateAsync({
          userId: user.id,
          data: {
            printed_samples: {
              ...(printStatus?.printed_samples ?? {
                unit: false,
                carton: false,
                hangtag: false,
              }),
              [type]: true,
            },
          },
        });
      });
    } catch (error) {
      console.error(`Error printing ${type}:`, error);
      toast.error(`Failed to print ${type} label. Please try again.`);
    }
  };

  return (
    <Page.Container>
      <Header.Major icon="printer" title="Printing Setup" />
      <Section>
        <Page.Content className="overflow-y-auto overflow-x-hidden">
          <P
            weight="medium"
            className="text-[36px] leading-[40px] custom-letter-spacing"
          >
            Start printing with 5 easy steps.
          </P>
          {user.vendor?.printing_onboarding?.pending_approval_reason &&
            !user.vendor?.printing_onboarding?.approved_at && (
              <Feedback
                title="Pending Approval"
                description={`Pending approval reason: ${user.vendor?.printing_onboarding?.pending_approval_reason}`}
                type="warning"
                className="mt-5"
              />
            )}
          {user.vendor?.printing_onboarding?.approved_at && (
            <Feedback
              title="Approved"
              description="You're ready to start printing labels for all of your POs."
              type="success"
              className="mt-5"
            />
          )}
          <P size="xl" className="mt-6" color="secondary">
            After you&apos;re set up and approved, you&apos;ll be able to print
            directly from your portal for all POs.
          </P>
          <P size="sm" className="leading-[20px] mt-4" color="secondary">
            Before you begin using printing on all POs, we need to complete a
            test run to ensure everything works as expected. Follow the steps
            below to get started.
          </P>
          <div className="flex justify-between gap-2 mt-7">
            {buttonData.map((button) => (
              <Button
                key={button.iconName}
                className="w-[200px] min-h-[50px]"
                disabled
              >
                <Icon name={button.iconName as IconProps['name']} />
                {button.label}
              </Button>
            ))}
          </div>

          <p className="mt-12 border-b nu-border-neutral-200" />

          <StepSection
            stepNumber="Step 1"
            stepTitle="Ensure you have the right printers and drivers."
            stepDescription={
              <>
                See{' '}
                <a
                  href={publicFilesUrls.printing_guide?.read || '#'}
                  className="text-primary-700 hover:text-primary-900"
                >
                  our guide
                </a>{' '}
                for Zebra printers, Drivers & Guidelines.
              </>
            }
            steps={stepOne}
          />

          <p className="mt-12 border-b nu-border-neutral-200" />

          <StepSection
            stepNumber="Step 2"
            stepTitle="Set up your printers"
            steps={stepTwo}
          />
          <div
            className="shadow-lg rounded-lg px-9 py-10 w-[500px] h-[300px] mt-10 relative"
            ref={containerRef}
          >
            <PrinterPopover containerRef={containerRef} />
          </div>

          <p className="mt-12 border-b nu-border-neutral-200" />

          <StepSection
            stepNumber="Step 3"
            stepTitle="Print test labels and ship them to us."
            steps={stepThree}
          />
          <div className="mt-3 ml-7 lg:ml-9">
            <P size="sm" weight="semibold">
              Attention - Mayra Gonzalez
            </P>
            <P size="sm" color="secondary">
              407 N Maple Dr
            </P>
            <P size="sm" color="secondary">
              Beverly Hills, CA 90210
            </P>
          </div>
          <Feedback
            title="If you have multiple locations where you print from, you must send a test from every location"
            description="This is a requirement for approval"
            type="warning"
            className="mt-10"
          />
          <div className="mt-10">
            <DataGrid
              paginationMode="client"
              hideFooter
              disableColumnReorder
              disableColumnResize
              autoHeight
              rows={[
                {
                  id: 1,
                  header: (
                    <P color="disabled" size="xs" weight="medium">
                      Print
                    </P>
                  ),
                  unit_label: (
                    <PrintButtonCell
                      printFn={() => handlePrintButtonClick('unit')}
                      isChecked={printStatus?.printed_samples?.unit || false}
                      printIcon="bag/paper-bag"
                    />
                  ),
                  hang_tags: (
                    <PrintButtonCell
                      printFn={() => handlePrintButtonClick('hangtag')}
                      isChecked={printStatus?.printed_samples?.hangtag || false}
                      printIcon="tag"
                    />
                  ),
                  carton_label: (
                    <PrintButtonCell
                      printFn={() => handlePrintButtonClick('carton')}
                      isChecked={printStatus?.printed_samples?.carton || false}
                      printIcon="package/package-02"
                    />
                  ),
                },
                {
                  id: 2,
                  header: (
                    <P color="disabled" size="xs" weight="medium">
                      Expected Output
                    </P>
                  ),
                  unit_label: (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        color="secondary"
                        onClick={() => setIsLightboxOpened('unit')}
                      >
                        Preview
                      </Button>
                      <Lightbox
                        slides={unitLabelPhotos}
                        isOpen={isLightboxOpened === 'unit'}
                        onClose={() => setIsLightboxOpened(null)}
                      />
                    </>
                  ),
                  hang_tags: (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        color="secondary"
                        onClick={() => setIsLightboxOpened('hangtag')}
                      >
                        Preview
                      </Button>
                      <Lightbox
                        slides={hangtagPhotos}
                        isOpen={isLightboxOpened === 'hangtag'}
                        onClose={() => setIsLightboxOpened(null)}
                      />
                    </>
                  ),
                  carton_label: (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        color="secondary"
                        onClick={() => setIsLightboxOpened('carton')}
                      >
                        Preview
                      </Button>
                      <Lightbox
                        slides={cartonLabelPhotos}
                        isOpen={isLightboxOpened === 'carton'}
                        onClose={() => setIsLightboxOpened(null)}
                      />
                    </>
                  ),
                },
              ]}
              columns={[
                {
                  field: 'header',
                  headerName: '',
                  render: 'custom',
                  display: 'flex',
                  flex: 1,
                  sortable: false,
                  minWidth: 130,
                  disableColumnMenu: true,
                },
                {
                  field: 'unit_label',
                  headerName: 'Print Unit Labels',
                  render: 'custom',
                  display: 'flex',
                  flex: 1,
                  sortable: false,
                  minWidth: 150,
                  disableColumnMenu: true,
                },
                {
                  field: 'hang_tags',
                  headerName: 'Print Hangtags',
                  render: 'custom',
                  display: 'flex',
                  flex: 1,
                  sortable: false,
                  minWidth: 150,
                  disableColumnMenu: true,
                },
                {
                  field: 'carton_label',
                  headerName: 'Print Carton Labels',
                  render: 'custom',
                  display: 'flex',
                  flex: 1,
                  sortable: false,
                  minWidth: 150,
                  disableColumnMenu: true,
                },
              ]}
            />
          </div>

          <p className="mt-12 border-b nu-border-neutral-200" />

          <StepSection
            stepNumber="Step 4"
            stepTitle="We'll notify you of next steps"
          />
          <P color="secondary" size="md" className="mt-5">
            Once we receive your test labels at the warehouse, we&apos;ll send
            you an email to let you know everything was working as expected or
            if we need to work together to do another test.
          </P>

          <p className="mt-12 border-b nu-border-neutral-200" />

          <StepSection stepNumber="Step 5" stepTitle="Get ready to print" />
          <P color="primary" size="md" weight="medium" className="mt-5">
            Watch our video series showing you the full process end-to-end.
          </P>
          <P color="secondary" size="md" className="mt-5">
            You can also view our{' '}
            <a
              href={publicFilesUrls.full_printing_guide?.read || '#'}
              className="text-primary-700 hover:text-primary-900"
            >
              full Printing Guide here.
            </a>
          </P>
          <div>
            {videoFiles &&
              Object.entries(videoFiles).map(([key, video]) => {
                const videoJsOptions = {
                  autoplay: false,
                  controls: true,
                  responsive: true,
                  fluid: true,
                  poster: video.thumbnail,
                  sources: [
                    {
                      src: video.read,
                      type: 'video/mp4',
                    },
                  ],
                };

                return <VideoJS key={key} options={videoJsOptions} />;
              })}
          </div>
        </Page.Content>
      </Section>
    </Page.Container>
  );
}
